import { useMemo, useCallback, useRef, type ReactNode } from "react";
import { FileText, Loader2, Expand, Clock, Camera, Upload } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { EntityWidgetProps } from "@/other/constants/EntityManagerConfig";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { CardTitle } from "@/modern/components/ui/card";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { EntityFileProvider, useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { DOCUMENTS_TILE_FILE_LIST_FILTERS } from "@/react/components/entityViews/files/components/editors/FileGridToolbar";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { FileDetailModal } from "@/react/components/entityViews/files/components/editors/FileDetailModal/FileDetailModal";
import { PresignedFilePreview } from "@/react/components/entityViews/files/components/PresignedFilePreview";
import { EntityFileDropOverlay } from "@/react/components/entityViews/files/components/EntityFileDropOverlay";
import { useEntityFileUpload } from "@/react/components/entityViews/files/hooks/useEntityFileUpload";
import { useFileDragAndDrop } from "@/react/components/entityViews/files/hooks/useFileDragAndDrop";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { cn } from "@/modern/utils";
import { useVirtualizerLoadMore } from "@/modern/hooks/useVirtualizerLoadMore";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { FileActionsMenu } from "@/react/components/entityViews/files/components/FileActionsMenu";
import type { FileViewerMode } from "@/react/components/entityViews/files/components/editors/FileDetailModal/types";

const transition = { duration: 0.15, ease: "easeInOut" as const };
/** Approx DocumentRow height (py-3 + thumb + meta). Refined by measureElement. */
const DOCUMENT_ROW_ESTIMATE_PX = 72;

const EXT_COLORS: Record<string, string> = {
  PDF: "bg-rose-500/10 text-rose-400",
  DOC: "bg-sky-500/10 text-sky-400",
  DOCX: "bg-sky-500/10 text-sky-400",
  XLS: "bg-emerald-500/10 text-emerald-400",
  XLSX: "bg-emerald-500/10 text-emerald-400",
  PPT: "bg-amber-500/10 text-amber-400",
  PPTX: "bg-amber-500/10 text-amber-400",
  TXT: "bg-zinc-500/10 text-zinc-400",
  CSV: "bg-teal-500/10 text-teal-400",
};

const getFileExtension = (name: string): string => {
  const parts = name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "";
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const DocumentRow = ({
  file,
  onOpen,
  onExtract,
  onSummaryShare,
  onDownload,
  onDelete,
}: {
  file: SwivuFile;
  onOpen: () => void;
  onExtract: () => void;
  onSummaryShare: () => void;
  onDownload: () => void;
  onDelete: () => void;
}) => {
  const ext = getFileExtension(file.name);
  const colorClass = EXT_COLORS[ext] || "bg-zinc-500/10 text-zinc-400";

  return (
    <button
      type="button"
      aria-label={file.name || "Open document"}
      onClick={onOpen}
      className="group flex w-full items-start gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left rounded-sm"
    >
      <div className="relative flex size-9 shrink-0 items-center justify-center rounded-md overflow-hidden bg-muted [&_*]:!text-[0px]">
        <PresignedFilePreview
          file={file}
          className="size-9 rounded-md object-cover"
          fileIconClassName="size-4"
          fileIconContainerClassName="size-9"
          compactLoading
          showLoadingLabel={false}
          isThumbnail
        />
      </div>
      <div className="flex-1 min-w-0">
        {file.isOptimistic ? (
          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
            <span className="truncate">Uploading...</span>
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <p className="truncate text-sm font-medium text-foreground/95 min-w-0">{file.name}</p>
                {ext && (
                  <span
                    className={cn(
                      "shrink-0 inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider leading-none",
                      colorClass,
                    )}
                  >
                    {ext}
                  </span>
                )}
              </div>
              <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <FileActionsMenu
                  file={file}
                  onFileDownload={() => onDownload()}
                  onFileDelete={() => onDelete()}
                  onView={() => onOpen()}
                  onExtract={() => onExtract()}
                  onSummaryShare={() => onSummaryShare()}
                />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-1.5 overflow-hidden min-w-0">
              {file.createdAt && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                      <Clock className="size-3 shrink-0" />
                      <span className="whitespace-nowrap">{formatDate(file.createdAt)}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{formatDate(file.createdAt)}</TooltipContent>
                </Tooltip>
              )}
              {file.source === "companycam" && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                  <Camera className="size-3 shrink-0" />
                  <span className="whitespace-nowrap">CompanyCam</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </button>
  );
};

const estimateDocumentRowSize = () => DOCUMENT_ROW_ESTIMATE_PX;

const VirtualizedDocumentList = ({
  files,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  renderRow,
}: {
  files: SwivuFile[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  renderRow: (file: SwivuFile) => ReactNode;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: files.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: estimateDocumentRowSize,
    overscan: 5,
    getItemKey: (index) => files[index]?.id ?? index,
  });

  useVirtualizerLoadMore(virtualizer, { hasNextPage, isFetchingNextPage, fetchNextPage });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
      <div
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        <div
          className="absolute left-0 top-0 w-full [&>*+*]:border-t [&>*+*]:border-border/50"
          style={{ transform: `translateY(${virtualItems[0]?.start ?? 0}px)` }}
        >
          {virtualItems.map((virtualItem) => {
            const file = files[virtualItem.index];
            if (!file) return null;
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
              >
                {renderRow(file)}
              </div>
            );
          })}
        </div>
      </div>
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

const EntityDocumentViewContent = ({
  onClose,
  headerTitle,
  entityType,
  dashboardColumns = 4,
  dashboardColSpan,
}: EntityWidgetProps) => {
  const {
    files,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoadingFiles,
    isErrorFiles,
    refetch,
    entityRecord,
    handleFileOpen,
    openedFile,
    openedFileMode,
    clearOpenedFileMode,
    isFileEditorOpen,
    setIsFileEditorOpen,
    uploadFiles,
    isUploading: isContextUploading,
    isImportingCompanyCam,
    handleFileDownload,
    deleteFile,
  } = useEntityFileContext();

  const { editorHandler } = useSwivuBryntumContext();
  const { uploadRef, isUploading: isLocalUploading, openUpload, handleUpload, accept } = useEntityFileUpload({
    category: "documents",
    uploadNewFile: uploadFiles,
  });
  const isUploading = isLocalUploading || isContextUploading;

  const { isDraggingOver, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useFileDragAndDrop({
    category: "documents",
    uploadFiles,
  });

  /** Extra empty-state upload when the tile is wider than 25% of the dashboard. */
  const colSpan = dashboardColSpan ?? dashboardColumns;
  const showExpandedControls = dashboardColumns > 0 && colSpan / dashboardColumns > 0.25;

  const documentFiles = useMemo(() => files.filter((f) => f.fileType === "document"), [files]);

  const openWithMode = useCallback(
    (file: SwivuFile, mode: FileViewerMode = "default") => {
      handleFileOpen(file, { mode });
    },
    [handleFileOpen],
  );

  const handleManage = useCallback(() => {
    editorHandler.openEditor(EDITOR_KEYS.taskFilesEditor, entityRecord, {
      entity: entityRecord,
      entityType,
      initialTab: "documents",
    });
  }, [editorHandler, entityRecord, entityType]);

  const isOpenedFileInDocumentList = useMemo(
    () => openedFile != null && documentFiles.some((f) => f.id == openedFile.id),
    [openedFile, documentFiles],
  );

  return (
    <>
      <DashboardCard
        onClose={onClose}
        cardContentClassName={cn(
          "h-full min-h-0 flex flex-col overflow-hidden p-0",
          isDraggingOver && "ring-2 ring-primary ring-offset-2",
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        header={
        <div className="flex items-center justify-between w-full gap-2">
          <CardTitle className="text-base gap-2 flex items-center min-w-0 overflow-hidden">
            <span className="truncate">{headerTitle ?? "Documents"}</span>
            {totalCount > 0 && (
              <Badge variant="muted" size="xs" className="shrink-0">
                {totalCount}
              </Badge>
            )}
          </CardTitle>

          <div className="flex items-center gap-2 shrink-0">
            {isUploading && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                <Loader2 className="size-3 animate-spin shrink-0" />
                Uploading…
              </span>
            )}
            {!isUploading && isImportingCompanyCam && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                    <Loader2 className="size-3 animate-spin shrink-0" />
                    Importing…
                  </span>
                </TooltipTrigger>
                <TooltipContent>Importing from CompanyCam…</TooltipContent>
              </Tooltip>
            )}
            <ButtonGroup>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="entityHeader" className="w-auto px-3" size="icon" onClick={handleManage}>
                    <Expand className="size-4" /> Manage
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Manage documents</TooltipContent>
              </Tooltip>
            </ButtonGroup>
            {showExpandedControls && (
              <input ref={uploadRef} type="file" className="hidden" multiple accept={accept} onChange={handleUpload} />
            )}
          </div>
        </div>
      }>
        {isDraggingOver && <EntityFileDropOverlay label="Drop documents here" />}
        <AnimatePresence mode="wait">
          {isLoadingFiles ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition} className="p-3 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-1">
                  <Skeleton className="size-9 rounded-md shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-3/4 rounded" />
                    <Skeleton className="h-3 w-1/3 rounded" />
                  </div>
                </div>
              ))}
            </motion.div>
          ) : isErrorFiles ? (
            <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition} className="flex flex-1 items-center justify-center h-full p-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-sm text-muted-foreground">Failed to load documents.</p>
                <Button size="sm" variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </motion.div>
          ) : documentFiles.length === 0 ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition} className="flex flex-1 items-center justify-center h-full p-4">
              <div className="h-full w-full">
                <div className="min-h-[12rem] h-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-center shadow-sm">
                  {isImportingCompanyCam ? (
                    <>
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Loader2 className="size-5 animate-spin" />
                      </span>
                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold text-foreground">Importing from CompanyCam</h3>
                        <p className="text-xs text-muted-foreground">Documents will appear here shortly.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <FileText className="size-5" />
                      </span>
                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold text-foreground">No documents yet</h3>
                        <p className="text-xs text-muted-foreground">
                          {showExpandedControls ? "Upload documents to see them here." : "Open Manage to upload or link CompanyCam."}
                        </p>
                      </div>
                      {showExpandedControls ? (
                        <Button size="sm" onClick={openUpload}>
                          <Upload className="size-4 mr-1" />
                          Upload
                        </Button>
                      ) : (
                        <Button size="sm" onClick={handleManage}>
                          <Expand className="size-4 mr-1" />
                          Manage
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transition}
              className="flex flex-1 min-h-0 flex-col"
            >
              <VirtualizedDocumentList
                files={documentFiles}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                renderRow={(file) => (
                  <DocumentRow
                    file={file}
                    onOpen={() => openWithMode(file)}
                    onExtract={() => openWithMode(file, "extract")}
                    onSummaryShare={() => openWithMode(file, "summaryShare")}
                    onDownload={() => handleFileDownload(file)}
                    onDelete={() => {
                      void deleteFile(file.id);
                    }}
                  />
                )}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </DashboardCard>

      {openedFile && isOpenedFileInDocumentList && (
        <FileDetailModal
          entityRecord={entityRecord}
          file={openedFile}
          navigationFiles={documentFiles}
          open={isFileEditorOpen}
          setOpen={(next) => {
            setIsFileEditorOpen(next);
            if (!next) clearOpenedFileMode();
          }}
          initialViewerMode={openedFileMode}
        />
      )}
    </>
  );
};

export const EntityDocumentView = ({
  record,
  entityType,
  onClose,
  headerTitle,
  dashboardColumns,
  dashboardColSpan,
}: EntityWidgetProps) => (
  <EntityFileProvider record={record} entityType={entityType} filters={DOCUMENTS_TILE_FILE_LIST_FILTERS}>
    <EntityDocumentViewContent
      onClose={onClose}
      headerTitle={headerTitle}
      record={record}
      entityType={entityType}
      dashboardColumns={dashboardColumns}
      dashboardColSpan={dashboardColSpan}
    />
  </EntityFileProvider>
);
